import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Zap, Check, Clock } from 'lucide-react';
import { get } from 'lodash';

import { ShieldCheck, Hourglass, FilesStack } from 'components/Icons';
import Alert from 'components/Alert';
import { Button } from 'components/Button';
import { FormRow } from 'components/Form';
import GeneralError from 'components/GeneralError';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction } from 'actions/workflow';

const ConnectBank = ({
  navigate,
  dispatch,
  searchParams,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('instant');
  const nextButtonRef = useRef(null);

  const cardData = {
    instant: {
      icon: Zap,
      title: 'Instant Verification',
      duration: '2 min',
      description: <><span className="font-semibold text-blue-500">Special Offer* </span> Verify your account now to remain eligible for a <span className="font-semibold text-blue-500">0.25%</span> promotional interest rate reduction.</>,
      features: [
        { icon: ShieldCheck, text: 'Bank-level security' },
        { icon: Check, text: 'No documents needed' }
      ],
      enforceChoice: true,
    },
    manual: {
      icon: Clock,
      title: 'Manual Verification',
      duration: '3-5 days',
      description: <>By proceeding with manual verification, the <span className="font-semibold">promotional interest rate reduction* will not</span> be applied.</>,
      features: [
        { icon: FilesStack, text: 'Upload bank statements' },
        { icon: Hourglass, text: 'Manual review process' }
      ],
      enforceChoice: false,
    }
  };

  const handleSelectMethod = (method) => {
    setSelectedMethod(method);
    setTimeout(() => {
      const el = nextButtonRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const offset = 40;
        window.scrollBy({ top: rect.bottom - window.innerHeight + offset, behavior: 'smooth' });
      }
    }, 0);
  }

  const renderCard = (method) => {
    const {
      icon: Icon,
      title,
      duration,
      description,
      features,
      enforceChoice,
    } = cardData[method]

    const isSelected = selectedMethod === method
    const tone = enforceChoice ? "blue" : "neutral"

    return (
      <button
        key={method}
        onClick={() => handleSelectMethod(method)}
        className={`group relative w-full overflow-hidden rounded-xl border-2 text-left transition-all
          ${
            isSelected
              ? tone === "blue"
                ? "scale-[1.01] border-blue-400"
                : "scale-[1.01] border-gray-400"
              : tone === "blue"
                ? "border-blue-300/50 hover:border-blue-400"
                : "border-gray-300/50 hover:border-gray-400"
          }
        `}
      >
        <div className="relative py-5 px-3 sm:px-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-6 md:h-9 w-6 md:w-9 shrink-0 items-center justify-center rounded-full
                  ${
                    tone === "blue"
                      ? "bg-blue-500"
                      : "bg-neutral-400"
                  }
                `}
              >
                <Icon
                  className={`h-3 md:h-5 w-3 md:w-5 text-white  ${
                    tone === "blue" ? "stroke-[1px] fill-white" : ""
                  }`}
                />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground">{title}</h3>
            </div>

            <div
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wide
                ${
                  tone === "blue"
                    ? "text-blue-500 bg-sky-100/85"
                    : "text-gray-500 bg-neutral-100"
                }
              `}
            >
              {duration}
            </div>
          </div>

          {get(checkinData, 'repaymentType') !== 'No Payment' ? (
            <div
              className={`mt-4 rounded-lg px-5 py-3 ${
                tone === "blue" ? "bg-sky-100/85" : "bg-slate-50"
              }`}
            >
              <p className="text-xs md:text-sm text-slate-900">
                {description}
              </p>
            </div>
          ) : null}

          <div className="mt-6 mb-2 flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-12">
            {features.map((item, index) => {
              const FeatureIcon = item.icon
              return (
                <div key={index} className="flex items-center gap-2 text-xs md:text-sm">
                  <FeatureIcon
                    className={`h-5 w-5 ${
                      tone === "blue"
                        ? "text-blue-600"
                        : "text-neutral-500"
                    }`}
                  />
                  <span className="text-neutral-500">{item.text}</span>
                </div>
              )
            })}
          </div>
        </div>
      </button>
    )
  }

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  }

  const handleReviewStatements = () => {
    setIsReviewSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {
        redirectToReview: true,
      },
      success: (res) => {
        setIsReviewSubmitting(false);
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
      },
      fail: (err) => {
        handleFail(err);
        setIsReviewSubmitting(false);
      },
    }));
  };

  const handleNext = () => {
    setIsSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {
        uploadBankStatement: selectedMethod === 'manual',
      },
      success: (res) => {
        setIsSubmitting(false);
        const nextUrl = get(res, 'data.url')
        if (nextUrl) navigate(nextUrl);
      },
      fail: handleFail,
    }));
  };

  return (
    <div className="flex flex-col items-center text-center gap-8">
      <div className="w-full mt-2">
        <div className="space-y-4">
          <Alert
            variant="warning"
            iconSize={3}
            forceCenter
            className="!p-2"
            textWrapperClass="!flex-initial"
          >
            <b className="text-sm">Checking and Savings Only</b>
          </Alert>
          {Object.keys(cardData).map(renderCard)}
        </div>
      </div>

      <GeneralError className="w-full text-left" error={generalError} />

      <FormRow ref={nextButtonRef} className="w-full md:flex-col">
        <Button
          className="flex-1"
          label={selectedMethod === 'instant' ? 'Verify Now' : 'Upload Statements'}
          onClick={handleNext}
          isLoading={isSubmitting}
          isDisabled={isReviewSubmitting}
        />
        {get(checkinData, 'hasActiveBankStatements') ? (
          <button
            type="button"
            onClick={handleReviewStatements}
            disabled={isReviewSubmitting}
            className="text-sm text-blue-500 transition underline hover:text-blue-700 disabled:opacity-50"
          >
            {isReviewSubmitting ? 'Loading...' : 'Review Statements'}
          </button>
        ) : null}
      </FormRow>
    </div>
  );
};

ConnectBank.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default ConnectBank;
